// Pure, framework-free helpers for the Book-an-Expert discovery (WP-X5). No React/service imports
// so they are trivially unit-testable; the pages wire them up.

export interface ExpertArea {
  id: number
  name: string
}

// Shape = X4's ExpertProfilePublicSerializer (camelCased): id, ownerType, displayName, headline,
// bio, listedRate, currency, rateType, availabilityNote, expertiseAreas[{id,name}]. No avatar field.
export interface Expert {
  id: number
  ownerType?: 'member' | 'partner' | string
  displayName?: string
  headline?: string
  bio?: string | null
  listedRate?: number | null
  currency?: string | null
  rateType?: string | null
  availabilityNote?: string | null
  expertiseAreas?: ExpertArea[]
  // Present on the full/self read (ExpertProfileSerializer) — not the public browse serializer.
  isListed?: boolean
  active?: boolean
  // The underlying expert reference the engagement-create endpoint needs. X4's public serializer
  // does NOT expose these yet (only the profile id/ownerType/displayName) — FLAGGED: X4 should add
  // one of these so "Request engagement" can create against #43.
  expertMemberId?: number | null
  expertPartnerId?: number | null
}

const CURRENCY_SYMBOLS: Record<string, string> = {usd: '$', eur: '€', gbp: '£'}

// Format a listed rate for display, optionally with the rate type (e.g. "$250 / hour"). A
// null/absent rate reads "Rate on request" (never a bare "0").
export const formatRate = (
  rate?: number | null,
  currency?: string | null,
  rateType?: string | null
): string => {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return 'Rate on request'
  const code = (currency || 'usd').toLowerCase()
  const symbol = CURRENCY_SYMBOLS[code]
  const amount = Number(rate).toLocaleString()
  const base = symbol ? `${symbol}${amount}` : `${code.toUpperCase()} ${amount}`
  return rateType ? `${base} / ${rateType}` : base
}

// Owner type → label ('member'/'partner' per X4's get_owner_type).
export const ownerTypeLabel = (type?: string): string =>
  type === 'partner' ? 'Partner' : type === 'member' ? 'Member' : ''

export interface AreaOption {
  value: string
  label: string
}

// Distinct expertise-area options derived from the loaded experts — used as a fallback for the
// filter when the professional-interests catalog can't be fetched.
export const deriveAreaOptions = (experts: Expert[]): AreaOption[] => {
  const map = new Map<string, string>()
  for (const e of experts) {
    for (const a of e.expertiseAreas ?? []) {
      map.set(String(a.id), a.name)
    }
  }
  return Array.from(map, ([value, label]) => ({value, label})).sort((a, b) =>
    a.label.localeCompare(b.label)
  )
}

// Normalize a professional-interests catalog row ({id, name}) into an area filter option.
export const catalogToAreaOptions = (rows: Array<{id: number; name: string}>): AreaOption[] =>
  (rows ?? []).filter((r) => r && r.id != null).map((r) => ({value: String(r.id), label: r.name}))
